import { z } from 'zod/v4';

import { UpdateFlexibleCalculationPlanRuntype } from '../flexibleCalculation/flexibleCalculationBackendTypes';
import { endpoint } from '../http/endpoint';
import {
    QuotationTemplateSectionRuntype,
    QuotationTemplateSlotMappingRuntype,
} from '../quotationTemplate/quotationTemplateBackendTypes';

const TemplateVariableTypeRuntype = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text') }),
    z.object({ kind: z.literal('multiline') }),
    z.object({ kind: z.literal('boolean') }),
    z.object({ kind: z.literal('color') }),
    z.object({ kind: z.literal('image') }),
    z.object({
        kind: z.literal('slider'),
        min: z.number(),
        max: z.number(),
        step: z.number(),
        unit: z.string().optional(),
    }),
    z.object({ kind: z.literal('enum'), options: z.array(z.string()) }),
    z.object({ kind: z.literal('table'), columns: z.array(z.string()) }),
]);

const ReferenceTemplateRuntype = z.object({ id: z.string(), name: z.string(), body: z.string() });

export const gatewayEndpoints = {
    'POST /gateway/llm/pcb/offers': endpoint({
        description: 'Extract the PCB offer details from a file',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        // z.any(): `azureAnalyzeResult` has no schema; `extraction` is llm-contracts' `customPartOfferDataSchema`.
        responseBody: z.any(),
        base: 'gateway',
    }),
    'POST /gateway/llm/off-the-shelf/offers': endpoint({
        description: 'Extract the off-the-shelf part offer details from a file',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        // z.any(): `azureAnalyzeResult` has no schema; `extraction` is llm-contracts' `offTheShelfOfferDataSchema`.
        responseBody: z.any(),
        base: 'gateway',
    }),
    'POST /gateway/llm/pcb/specifications': endpoint({
        description: 'Extract the PCB specification details from a file',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        // z.any(): `azureAnalyzeResult` has no schema; `extraction` is llm-contracts' `pcbSpecificationDataSchema`.
        responseBody: z.any(),
        base: 'gateway',
    }),
    'POST /gateway/llm/quotation-template/chat': endpoint({
        description: 'Chat with the quotation template LLM for structured actions',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            template: z.string(),
            variables: z.record(z.string(), z.string()),
            sections: z.array(
                z.object({
                    id: z.string(),
                    label: z.string(),
                    enabled: z.boolean(),
                }),
            ),
            dataJson: z.string(),
            message: z.string(),
            history: z
                .array(
                    z.object({
                        role: z.enum(['user', 'assistant']),
                        content: z.string(),
                    }),
                )
                .optional(),
        }),
        responseBody: z.object({
            actions: z.array(z.any()),
            explanation: z.string(),
        }),
        base: 'gateway',
    }),
    'POST /gateway/llm/quotation-template/compile': endpoint({
        description: 'Compile a quotation template and render its pages',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            body: z.string(),
            data: z.unknown().optional(),
            inputs: z.record(z.string(), z.string()).optional(),
            pages: z.array(z.number().int().positive()).optional(),
            ppi: z.number().positive().max(300).optional(),
        }),
        responseBody: z.object({
            ok: z.boolean(),
            pages: z.number(),
            // Positions are 1-indexed and point into the submitted body, not the
            // document the compiler saw.
            errors: z.array(z.object({ line: z.number(), col: z.number(), message: z.string() })),
            warnings: z.array(z.object({ line: z.number(), col: z.number(), message: z.string() })),
            /** base64 PNG per rendered page. */
            images: z.array(z.string()),
            /** base64 PDF; empty when the compile failed. */
            pdf: z.string(),
            /** True when the preview compiled against less data than was submitted. */
            trimmed: z.boolean(),
        }),
        base: 'gateway',
    }),
    'POST /gateway/llm/quotation-template/validate-directives': endpoint({
        description: 'Check a quotation template body against the slot mappings, inputs and sections it is saved with',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            body: z.string(),
            slot_mappings: z.array(QuotationTemplateSlotMappingRuntype).optional(),
            inputs: z.record(z.string(), z.string()).optional(),
            sections: z.array(QuotationTemplateSectionRuntype).optional(),
        }),
        responseBody: z.object({
            slots: z.array(z.object({ key: z.string(), kind: z.enum(['single', 'array']) })),
            inputs: z.array(z.object({ key: z.string(), type: TemplateVariableTypeRuntype, default: z.string() })),
            assembly_fields: z.array(
                z.object({ key: z.string(), type: z.enum(['text', 'enum']), options: z.array(z.string()).optional() }),
            ),
            used_assembly_fields: z.array(z.string()),
            sections: z.array(QuotationTemplateSectionRuntype),
            multi_plan: z.boolean(),
            problems: z.array(z.object({ code: z.string(), message: z.string(), subject: z.string().optional() })),
        }),
        base: 'gateway',
    }),
    'GET /gateway/llm/quotation-template/references': endpoint({
        description: 'List the reference quotation templates',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ templates: z.array(ReferenceTemplateRuntype.omit({ body: true })) }),
        base: 'gateway',
    }),
    'GET /gateway/llm/quotation-template/references/:id': endpoint({
        description: 'Get the Typst body of a reference quotation template',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: ReferenceTemplateRuntype,
        base: 'gateway',
    }),
    'POST /gateway/llm/quotation-template/from-pdf': endpoint({
        description: 'Generate a quotation template from an uploaded PDF',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        responseBody: z.object({
            template: z.string(),
            dataJson: z.string(),
            explanation: z.string(),
        }),
        base: 'gateway',
    }),
    'POST /gateway/llm/flexible-calculation/formula/chat': endpoint({
        description: 'Chat with formula - create, edit, or fix formulas using LLM',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            message: z.string(),
            formula: z.string().optional(),
            lineItemNames: z.array(z.string()).optional(),
            drivers: z
                .array(
                    z.object({
                        name: z.string(),
                        formulaValue: z.string(),
                    }),
                )
                .optional(),
            errors: z
                .array(
                    z.object({
                        message: z.string(),
                        from: z.number(),
                        to: z.number(),
                    }),
                )
                .optional(),
        }),
        responseBody: z.object({
            formula: z.string().optional(),
            explanation: z.string().optional(),
        }),
        base: 'gateway',
    }),
    'POST /gateway/llm/flexible-calculation/plans/diagnose-error': endpoint({
        description: 'Diagnose a plan save error using AI',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            plan: UpdateFlexibleCalculationPlanRuntype,
            error: z.object({ status: z.number(), body: z.unknown() }),
        }),
        responseBody: z.object({ message: z.string(), resolution: z.string() }),
        base: 'gateway',
    }),
    'POST /gateway/llm/flexible-calculation/templates/map-conversion': endpoint({
        description: 'Generate row-level mapping between a legacy template and its flexible calculation conversion',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            legacyTemplate: z.any(),
            flexibleTemplate: z.any(),
        }),
        responseBody: z.object({
            mappings: z.array(
                z.object({
                    oldRowPath: z.string(),
                    oldRowLabel: z.string(),
                    newItemId: z.string().nullable(),
                    confidence: z.enum(['high', 'medium', 'low']),
                    notes: z.string(),
                }),
            ),
            unmappedNewItemIds: z.array(z.string()),
        }),
        base: 'gateway',
    }),
};
